"use client"

import { useEffect, useRef } from "react"
import { Check, CheckCheck } from "lucide-react"

import { cn } from "@/lib/utils"
import { useArticleRead } from "@/lib/articles/read"
import { marketingActionClass } from "@/components/marketing/marketing-action"
import { useToast } from "@/components/ui/toast"

interface AutoMarkReadProps {
  slug: string
}

/**
 * Mounted on the article reader. Marks the article as read once the
 * user has either:
 *   (a) scrolled to the bottom of the page, OR
 *   (b) dwelled on the page for 30 seconds.
 *
 * Whichever comes first. Both signals roughly mean "they actually
 * engaged with the article." A 30-second dwell on a 5-min read is
 * about 10% of the way through — generous enough that someone who
 * skim-scrolled gets credited.
 *
 * Renders nothing.
 */
export function AutoMarkRead({ slug }: AutoMarkReadProps) {
  const { read, mark } = useArticleRead(slug)
  const markedRef = useRef(false)

  useEffect(() => {
    if (read || markedRef.current) return

    const dwellTimer = window.setTimeout(() => {
      if (markedRef.current) return
      markedRef.current = true
      mark()
    }, 30_000)

    function checkScroll() {
      if (markedRef.current) return
      const reachedBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 200
      if (reachedBottom) {
        markedRef.current = true
        mark()
        window.clearTimeout(dwellTimer)
      }
    }

    window.addEventListener("scroll", checkScroll, { passive: true })
    checkScroll()
    return () => {
      window.clearTimeout(dwellTimer)
      window.removeEventListener("scroll", checkScroll)
    }
  }, [slug, read, mark])

  return null
}

interface MarkReadButtonProps {
  slug: string
  className?: string
}

/**
 * Manual mark/unmark toggle. Shown on the article reader.
 *
 * Also wires a global "press R" keyboard shortcut. Listening here means
 * the shortcut is only active while the article reader is mounted, which
 * is what we want — pressing R on the homepage shouldn't mark anything.
 */
export function MarkReadButton({ slug, className }: MarkReadButtonProps) {
  const { read, toggle } = useArticleRead(slug)
  const toast = useToast()
  const Icon = read ? CheckCheck : Check

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "r" && e.key !== "R") return
      // Don't fire while the user is typing in a field.
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      e.preventDefault()
      toggle()
      toast.push({
        tone: "success",
        message: read ? "Marked unread." : "Marked read.",
      })
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [toggle, read, toast])

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={read}
      title={read ? "Mark unread (R)" : "Mark read (R)"}
      className={cn(
        // The shared action class carries font-semibold; on the reading
        // surface a control label is the one bold weight, 800 (twMerge keeps
        // only this class, so the rendered weight is the one written here).
        marketingActionClass({ size: "sm" }),
        "font-extrabold",
        read
          ? "border-[var(--color-vermilion)] text-[var(--color-vermilion)] hover:border-[var(--color-oxblood)] hover:text-[var(--color-oxblood)]"
          : "text-[var(--color-ink-muted)]",
        className,
      )}
    >
      <Icon size={13} aria-hidden />
      {read ? "Read" : "Mark read"}
    </button>
  )
}
